
import React, { Component } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  Grid,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  FitnessCenter as FitnessIcon,
  Group as GroupIcon,
  EmojiEvents as TrophyIcon,
  WorkspacePremium as CertIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import TrainerApplicationModal from '../components/forms/TrainerApplicationModal';

// Higher-order component to provide hooks to class component
const withHooks = (Component) => {
  return (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return <Component {...props} theme={theme} isMobile={isMobile} />;
  };
};

class CareerPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalOpen: false
    };
  }

  handleModalOpen = () => {
    this.setState({ modalOpen: true });
  };

  handleModalClose = () => {
    this.setState({ modalOpen: false });
  };

  render() {
    const { theme } = this.props;

    const values = [
      {
        icon: <FitnessIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
        title: "Strength First 💪",
        description: "We never lose sight of what builds champions and transforms lives."
      },
      {
        icon: <TrophyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
        title: "Honor the Legacy 🏆",
        description: "We're driven by an unending pursuit of excellence and tradition."
      },
      {
        icon: <GroupIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
        title: "Build Community 🤝",
        description: "We cultivate unity as the core of achievement and lasting success."
      },
      {
        icon: <CertIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
        title: "Quality & Integrity ✅",
        description: "We're so not corporate—and we like it that way."
      }
    ];

    const perks = [
      { icon: <CheckIcon />, text: "State-of-the-art strength equipment" },
      { icon: <CheckIcon />, text: "Private and semi-private training spaces" },
      { icon: <CheckIcon />, text: "Flexible rental options (hourly/monthly)" },
      { icon: <CheckIcon />, text: "Digital client management tools" }
    ];

    return (
      <Box id="main-content">
        {/* Hero Section */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 600,
                  mb: 3,
                  maxWidth: '100%',
                  textAlign: 'center'
                }}
              >
                Build <Box component="span" sx={{ color: theme.palette.primary.main }}>the next generation</Box><br />
                of fitness champions
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 600, mx: 'auto' }}>
                We provide trainers with the tools to bring transformative fitness experiences to life with unrivaled equipment and community support.
              </Typography>
            </Box>
          </Container>
        </Box>

        <Divider sx={{ borderColor: 'grey.200' }} />

        {/* Values Section */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 6 }}>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 600, mb: 2, maxWidth: 500 }}>
                <Box component="span" sx={{ color: theme.palette.primary.main }}>Varsity Fitness</Box> Pact
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600 }}>
                The Varsity Fitness pact describes the values we embody as a gym, which help guide us toward the experiences and results we aim to deliver.
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'nowrap', overflowX: 'wrap' }}>
              {values.map((value, index) => (
                <Grid item xs={12}md={6} lg={3} key={index} sx={{ minWidth: '20%', flex: '0 0 20%' }}>
                  <Paper
                    sx={{
                      p: 2,
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {value.icon}
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {value.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
           {/* Open Roles Section */}
        <Box sx={{ mt:10, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: { xs: 'flex-start', md: 'space-between' }, alignItems: { xs: 'flex-start', md: 'flex-start' } }}>
              <Box sx={{ flex: 1, mb: { xs: 2, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: '2.5rem',
                    fontWeight: 600,
                    mb: 2,
                    maxWidth: 1000
                  }}
                  id="open-roles"
                >
                  Independent {' '}
                  <Box component="span" sx={{ color: theme.palette.primary.main }}>
                    Personal Trainer
                  </Box>
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  We are actively seeking certified personal trainers to join our community:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Join our team as a space-renting contractor in our Long Beach facility. Bring your expertise and build your client base in a supportive environment.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowIcon />}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', md: 'flex-end' } }}
                onClick={this.handleModalOpen}
              >
                Apply Now
              </Button>
            </Box>
          </Container>
        </Box>
        </Box>
        <TrainerApplicationModal
          open={this.state.modalOpen}
          onClose={this.handleModalClose}
        />
      </Box>
    );
  }
}

export default withHooks(CareerPage);
